import { Component, OnInit, OnDestroy, signal, inject, DestroyRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
// PrimeNG 21 Imports
import { MessageService, MenuItem, ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { Toast } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ColumnConfig, TABLE_CONFIG } from '../dashboard/table-config';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { ConfirmationDialogService } from '../../../core/services/confirmation-dialog.service';
import { ProfessionalsService } from '../../../core/services/professionals.service';
import { FormDropdownService } from '../../../core/services/form-dropdown.service';
import { DocumentsService } from '../../../core/services/documents.service';
import { ProfessionalSummaryResponse, ProfessionalsListFilters } from '../../../core/models/professional.models';

@Component({
  selector: 'app-professionals-registry',
  standalone: true,
  providers: [DialogService, MessageService],
  imports: [
    CommonModule,
    FormsModule,
    DynamicDialogModule,
    Toast,
    RegistryTableComponent,
    BreadcrumbModule,
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    SelectButtonModule,
  ],
  templateUrl: './professionals-registry.component.html',
  styleUrl: './professionals-registry.component.scss',
})
export class ProfessionalsRegistryComponent implements OnInit, OnDestroy {
  private dialogService = inject(DialogService);
  private professionalsService = inject(ProfessionalsService);
  private formDropdownService = inject(FormDropdownService);
  private documentsService = inject(DocumentsService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private confirmDialogService = inject(ConfirmationDialogService);
  private titleService = inject(Title);

  private filterSubject = new Subject<Record<string, any>>();
  private destroy$ = new Subject<void>();
  private currentTableFilters: Record<string, any> = {};

  // Table configuration
  tableTitle = 'Fiduciari Registrati';
  tableIcon = 'pi-user';
  columns: ColumnConfig[] = [];
  data = signal<any[]>([]);
  loading = signal<boolean>(false);
  breadcrumbItems = signal<MenuItem[]>([{ label: 'Registro Fiduciari' }]);
  hasActiveFilters = signal<boolean>(false);
  activeFilters = signal<{category?: string, status?: string}>({});
  /** Filtri iniziali da applicare visivamente sulla tabella (da queryParams dashboard) */
  initialFilters = signal<Record<string, any>>({});;

  // Export dialog
  showExportDialog = signal<boolean>(false);
  isExporting = signal<boolean>(false);
  selectedExportCategory = signal<string>('PROFESSIONALS_ALBO');

  readonly exportCategoryOptions = [
    { label: 'Tutto l\'albo', value: 'PROFESSIONALS_ALBO' },
    { label: 'Periti Auto',   value: 'SUP_REF_PERITO_AUTO_01' },
    { label: 'Periti RE',     value: 'SUP_REF_RE_EXPERT_01' },
    { label: 'Investigatori', value: 'SUP_REF_INVESTIGATOR_01' },
    { label: 'Medici',        value: 'SUP_REF_DOCTOR_01' },
    { label: 'Legali',        value: 'SUP_REF_LAWYER_01' },
    { label: 'Carrozzerie',   value: 'SUP_REF_BODY_SHOP_01' },
    { label: 'Carrozzerie — Big Data', value: 'BIG_DATA_BODY_SHOP_01' },
    { label: 'Carrozzerie RCI — Big Data', value: 'BIG_DATA_RCI_BODY_SHOP_01' },
    { label: 'Carrozzerie DB — Big Data', value: 'BIG_DATA_DB_BODY_SHOP_01' },
    { label: 'Carrozzerie — Webmaster', value: 'WEBMASTER_BODY_SHOP_01' },
    { label: 'Carrozzerie RCI — Webmaster', value: 'WEBMASTER_RCI_BODY_SHOP_01' },
    { label: 'Provider Grandine', value: 'SUP_REF_HAIL_PROVIDER_01' },
    { label: 'Network Carrozzerie', value: 'SUP_REF_NETWORK_01' },
    { label: 'Collaboratori (tutti)', value: 'COLLABORATORS_01' },
  ];

  private readonly EMPTY_VALUE = '-';
  private allProfessionalsData: any[] = [];

  ngOnInit(): void {
    this.titleService.setTitle('Registro Fiduciari - PalEasy');
    this.loadConfiguration();
    
    // Pre-carica le categorie fiduciario per decodifica sincrona
    this.formDropdownService.preloadTrusteeCategories().subscribe();

    // Server-side filtering debounced
    this.filterSubject.pipe(
      debounceTime(700),
      takeUntil(this.destroy$)
    ).subscribe(tableFilters => {
      this.loadProfessionals(this.mapTableFiltersToBeFilters(tableFilters));
    });
    
    // Leggi queryParams dalla dashboard
    this.route.queryParams.subscribe(params => {
      const category = params['category'];
      const dashboardStatus = params['status'];

      if (category || dashboardStatus) {
        const apiStatus = this.mapDashboardStatusToApi(dashboardStatus);
        this.activeFilters.set({ category, status: apiStatus });
        this.hasActiveFilters.set(true);

        // Costruisce initialFilters per la tabella visiva
        const tableFilters: Record<string, any> = {};
        if (category) tableFilters['type'] = category;
        if (apiStatus) tableFilters['professionalStatus'] = apiStatus;
        this.initialFilters.set(tableFilters);

        // Imposta il filtro corrente e chiama il BE
        this.currentTableFilters = { ...tableFilters };
        this.loadProfessionals(this.mapTableFiltersToBeFilters(this.currentTableFilters));
      } else {
        this.loadProfessionals();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Maps dashboard status values to API enum values
   * Dashboard uses italian labels, API uses english enums
   */
  private mapDashboardStatusToApi(dashboardStatus?: string): string | undefined {
    if (!dashboardStatus) return undefined;
    
    const statusMap: Record<string, string> = {
      'IN_LAVORAZIONE': 'IN_PROGRESS',
      'IN_ATTESA_DI_RISPOSTA': 'AWAITING_RESPONSE',
      'ACTIVE': 'ACTIVE',
      'SUSPENDED': 'SUSPENDED',
      'CEASED': 'CEASED'
    };
    
    return statusMap[dashboardStatus] || dashboardStatus;
  }

  private loadConfiguration(): void {
    const config = TABLE_CONFIG['elencoFiduciari'];
    if (config) {
      this.columns = config.columns;
    }
  }

  private loadProfessionals(filters?: ProfessionalsListFilters): void {
    this.loading.set(true);

    // Costruisce i filtri BE se presenti
    const hasFilters = filters && Object.keys(filters).length > 0;

    // Ordina per data dell'ultimo step di registrazione (workflow) più recente.
    // Lato BE 'updatedAt' è mappato su prs.updatedAt (step corrente), non su professional.updatedAt.
    this.professionalsService
      .getProfessionals(0, 5000, 'updatedAt,desc', hasFilters ? filters : undefined)
      .subscribe({
      next: (response) => {
        if (response?.status && Array.isArray(response?.data?.content)) {
          const mappedData = this.mapProfessionalsForTable(response.data.content);
          this.allProfessionalsData = mappedData;
          this.data.set(mappedData);

          if (hasFilters) {
            const filterDetails: string[] = [];
            if (filters?.type?.length) filterDetails.push(`Categoria: ${this.getCategoryDisplay(filters.type[0])}`);
            if (filters?.professionalStatus?.length) filterDetails.push(`Stato: ${this.getStatusDisplay(filters.professionalStatus[0])}`);
            if (filters?.businessName) filterDetails.push(`Nome: ${filters.businessName}`);
            if (filters?.identificationCode) filterDetails.push(`Codice: ${filters.identificationCode}`);
            if (filterDetails.length) {
              this.messageService.add({
                severity: 'info',
                summary: 'Filtri Applicati',
                detail: filterDetails.join(' - '),
                life: 3000,
              });
            }
          }
        } else {
          console.warn('Unexpected API response format:', response);
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Formato dati non valido ricevuto dal server',
            life: 4000,
          });
          this.data.set([]);
        }
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading professionals:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore nel recupero dei dati',
          detail: "Impossibile caricare l'elenco dei fiduciari. Riprova più tardi.",
          life: 5000,
        });
        this.data.set([]);
        this.loading.set(false);
      },
    });
  }

  /**
   * Maps API response (ProfessionalSummaryResponse) to table row data
   * API v0.0.4 returns lightweight DTOs with computed fields
   */
  private mapProfessionalsForTable(rows: ProfessionalSummaryResponse[]): any[] {
    return rows.map((row) => {
      // Format createdAt as DD/MM/YYYY
      let formattedDate = this.EMPTY_VALUE;
      if (row.createdAt) {
        try {
          const date = new Date(row.createdAt);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          formattedDate = `${day}/${month}/${year}`;
        } catch (e) {
          console.warn('Invalid date format:', row.createdAt);
        }
      }

      // COALESCE: businessName (legal) OR "lastName firstName" (physical)
      let displayName = this.EMPTY_VALUE;
      if (row.businessName) {
        displayName = row.businessName;
      } else if (row.lastName || row.firstName) {
        const parts = [row.lastName, row.firstName].filter(Boolean);
        displayName = parts.join(' ');
      }

      return {
        id: row.id,
        solCode: row.solCode,
        businessName: displayName,
        supplierName: displayName, // Used by table-config field
        identificationCode: row.identificationCode || this.EMPTY_VALUE,
        type: row.type,
        professionalStatus: row.professionalStatus,
        // Map professionalStatus to 'status' for registry-table action logic
        // IN_PROGRESS, AWAITING_RESPONSE, SUSPENDED → 'DRAFT' (shows edit pencil)
        // ACTIVE, CEASED → 'ACTIVE' (shows detail eye)
        status: this.mapProfessionalStatusToActionStatus(row.professionalStatus, row.currentStepKey),
        legalOrPhysical: row.legalOrPhysical,
        createdAt: formattedDate,
        currentStepKey: row.currentStepKey,
      };
    });
  }

  /**
   * Maps professionalStatus to generic status for registry-table action button logic
   * - DRAFT → shows edit pencil (for BO modifications)
   * - ACTIVE → shows detail eye (for readonly view)
   */
  /**
   * Step post-conferma MFA: una volta approvati i dati MFA il fiduciario prosegue
   * verso il contratto e non deve più essere modificabile né eliminabile
   * (solo visualizzazione dettaglio), anche se lo stato derivato è ancora IN_PROGRESS.
   */
  private static readonly MFA_CONFIRMED_STEPS = new Set([
    'CONTRACT_SENT',
    'CONTRACT_SIGNATURE',
    'CONTRACT_COUNTERSIGNATURE',
  ]);

  private mapProfessionalStatusToActionStatus(professionalStatus: string, currentStepKey?: string): string {
    // Dati MFA confermati → sola lettura (niente modifica/eliminazione)
    if (currentStepKey && ProfessionalsRegistryComponent.MFA_CONFIRMED_STEPS.has(currentStepKey)) {
      return 'ACTIVE'; // Read-only view
    }

    switch (professionalStatus) {
      case 'IN_PROGRESS':
      case 'AWAITING_RESPONSE':
      case 'SUSPENDED':
        return 'DRAFT'; // Editable by BO
      case 'ACTIVE':
      case 'CEASED':
      default:
        return 'ACTIVE'; // Read-only view
    }
  }

  // =====================
  // EVENT HANDLERS
  // =====================
  onDetailClick(rowData: any): void {
    if (rowData?.id) {
      // Naviga a review-professional (mode determinato automaticamente dal current step)
      this.router.navigate(['/axa/review-professional', rowData.id]);
    }
  }

  onEditClick(rowData: any): void {
    if (rowData?.id) {
      // Naviga a review-professional (mode determinato automaticamente dal current step)
      this.router.navigate(['/axa/review-professional', rowData.id]);
    }
  }

  onDeleteClick(rowData: any): void {
    console.log('onDeleteClick triggered with rowData:', rowData);
    if (!rowData?.id) return;

    const professionalName = rowData.displayName || rowData.professionalName || rowData.id;

    console.log('Calling confirmDialogService.confirmDelete...');
    this.confirmDialogService.confirmDelete(
      `Sei sicuro di voler eliminare il fiduciario "${professionalName}"? Questa azione non può essere annullata.`,
      () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Funzionalità in sviluppo',
          detail: 'Eliminazione fiduciario non ancora disponibile.',
          life: 3000,
        });
      },
    );
  }

  onExport(): void {
    this.selectedExportCategory.set('PROFESSIONALS_ALBO');
    this.showExportDialog.set(true);
  }

  confirmExport(): void {
    const modelId = this.selectedExportCategory();
    this.isExporting.set(true);
    this.documentsService.exportRegistry(modelId).subscribe({
      next: (response) => {
        this.isExporting.set(false);
        this.showExportDialog.set(false);
        const blob = response.body as Blob | null;
        if (!blob) {
          this.messageService.add({ severity: 'error', summary: 'Errore export', detail: 'File non ricevuto dal server', life: 4000 });
          return;
        }
        let fileName = 'Registro_Fiduciari.xlsx';
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^";]+)"?/);
          if (match && match[1]) fileName = decodeURIComponent(match[1]);
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.messageService.add({ severity: 'success', summary: 'Export completato', detail: 'Il file è stato scaricato con successo', life: 3000 });
      },
      error: () => {
        this.isExporting.set(false);
        this.messageService.add({ severity: 'error', summary: 'Errore export', detail: 'Impossibile esportare il registro fiduciari', life: 4000 });
      },
    });
  }

  onClearFilters(): void {
    // Reset filtri correnti
    this.currentTableFilters = {};
    // Rimuovi queryParams e ricarica tutti i dati
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
    this.loadProfessionals();
  }

  onTableFilterChange(event: { value: any; field: string; matchMode: string }): void {
    if (event.value === null || event.value === '' || event.value === undefined ||
        (Array.isArray(event.value) && event.value.length === 0)) {
      delete this.currentTableFilters[event.field];
    } else {
      this.currentTableFilters[event.field] = event.value;
    }

    // Select/dropdown: risposta immediata — Text: debounced (evita chiamate per ogni tasto)
    const col = this.columns.find(c => c.field === event.field);
    if (col?.filterType === 'select' || col?.filterType === 'multiselect') {
      this.loadProfessionals(this.mapTableFiltersToBeFilters({ ...this.currentTableFilters }));
    } else {
      this.filterSubject.next({ ...this.currentTableFilters });
    }
  }

  private mapTableFiltersToBeFilters(tableFilters: Record<string, any>): ProfessionalsListFilters {
    const beFilters: ProfessionalsListFilters = {};
    const toArray = (v: any) => Array.isArray(v) ? v : (v ? [v] : []);
    const types = toArray(tableFilters['type']);
    if (types.length) beFilters.type = types as ProfessionalsListFilters['type'];
    const statuses = toArray(tableFilters['professionalStatus']);
    if (statuses.length) beFilters.professionalStatus = statuses as ProfessionalsListFilters['professionalStatus'];
    const natures = toArray(tableFilters['legalOrPhysical']);
    if (natures.length) beFilters.legalOrPhysical = natures as ProfessionalsListFilters['legalOrPhysical'];
    if (tableFilters['supplierName']) beFilters.businessName = tableFilters['supplierName'];
    if (tableFilters['identificationCode']) beFilters.identificationCode = tableFilters['identificationCode'];
    if (tableFilters['id']) beFilters.id = tableFilters['id'];
    return beFilters;
  }
  
  /**
   * Decodifica il codice categoria usando il servizio centralizzato FormDropdownService
   * @param category Codice categoria (es. AUTO_EXPERT)
   * @returns Label tradotta (es. Perito Auto)
   */
  private getCategoryDisplay(category: string): string {
    return this.formDropdownService.decodeProfessionalTypeSync(category);
  }
  
  private getStatusDisplay(status: string): string {
    const statusMap: Record<string, string> = {
      // API enum values to Italian labels
      'IN_PROGRESS': 'In Lavorazione',
      'AWAITING_RESPONSE': 'In Attesa di Risposta',
      'ACTIVE': 'Attivo',
      'SUSPENDED': 'Sospeso',
      'CEASED': 'Cessato',
      // Legacy dashboard values (fallback)
      'IN_LAVORAZIONE': 'In Lavorazione',
      'IN_ATTESA_DI_RISPOSTA': 'In Attesa di Risposta',
      'DRAFT': 'Bozza',
    };
    return statusMap[status] || status;
  }

  goToProfessionalWizard(): void {
    this.router.navigate(['axa/professional-registration-wizard']);
  }

  // =====================
  // STATUS HELPERS - Ora centralizzato nel servizio
  // =====================
  // getStatusDisplay() rimosso - usa professionalsService.getStatusDisplay()
}
